import React from 'react'
import { parse, parseISO } from 'date-fns'
import { cva, cx, type VariantProps } from 'class-variance-authority'
import { type Row } from '@tanstack/react-table'
import { ListIcon } from '@phosphor-icons/react/dist/ssr/List'
import { SortAscendingIcon } from '@phosphor-icons/react/dist/ssr/SortAscending'
import { SortDescendingIcon } from '@phosphor-icons/react/dist/ssr/SortDescending'
import { FunnelIcon } from '@phosphor-icons/react/dist/ssr/Funnel'
import { FunnelSimpleIcon } from '@phosphor-icons/react/dist/ssr/FunnelSimple'
import { DotsThreeVerticalIcon } from '@phosphor-icons/react/dist/ssr/DotsThreeVertical'
import type { LabelType, SupportedLanguages } from '@skop-app/lib-domain/types'

import { Button } from '@/components/atoms/Button/button.atom'
import { DropdownMenu } from '@/components/atoms/DropdownMenu/dropdownMenu.atom'
import { DynamicIcon } from '@/components/atoms/DynamicIcon/dynamicIcon.atom'
import {
  DateRangePicker,
  type DateRangeFilterValue,
  type DateRangePickerReactSelectStyles,
} from '@/components/atoms/DateRangePicker/dateRangePicker.atom'
import { ScrollArea } from '@/components/atoms/ScrollArea/scrollArea.atom'
import { reactSelectTheme } from '../Select/select.config'

type Value = string | number

const tableRootVariants = cva('relative w-full overflow-hidden', {
  variants: {
    variant: {
      default: 'rounded-md border shadow-sm',
      light: '',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
})

const tableVariants = cva('w-full caption-bottom text-sm border-collapse', {
  variants: {
    variant: {
      default: '',
      light: '',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
})

type TableRootProps = React.HTMLAttributes<HTMLTableElement> &
  VariantProps<typeof tableRootVariants> &
  VariantProps<typeof tableVariants>

const TableRoot = React.forwardRef<HTMLTableElement, TableRootProps>(
  (props, ref) => {
    const { className, variant, ...rest } = props
    return (
      <ScrollArea className={cx(tableRootVariants({ variant }))}>
        <table
          ref={ref}
          className={cx(tableVariants({ variant }), className)}
          {...rest}
        />
      </ScrollArea>
    )
  },
)
TableRoot.displayName = 'TableRoot'

const tableHeaderVariants = cva('', {
  variants: {
    variant: {
      default: '[&_tr]:border-b bg-neutral-100',
      light: 'bg-neutral-50',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
})

type TableHeaderProps = React.HTMLAttributes<HTMLTableSectionElement> &
  VariantProps<typeof tableHeaderVariants>

const TableHeader = React.forwardRef<HTMLTableSectionElement, TableHeaderProps>(
  (props, ref) => {
    const { className, variant, ...rest } = props
    return (
      <thead
        ref={ref}
        className={cx(tableHeaderVariants({ variant }), className)}
        {...rest}
      />
    )
  },
)
TableHeader.displayName = 'TableHeader'

const tableBodyVariants = cva('divide-y', {
  variants: {
    variant: {
      default: 'divide-neutral-100',
      light: 'divide-neutral-50 bg-white',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
})

type TableBodyProps = React.HTMLAttributes<HTMLTableSectionElement> &
  VariantProps<typeof tableBodyVariants>

const TableBody = React.forwardRef<HTMLTableSectionElement, TableBodyProps>(
  (props, ref) => {
    const { className, variant, ...rest } = props
    return (
      <tbody
        ref={ref}
        className={cx(tableBodyVariants({ variant }), className)}
        {...rest}
      />
    )
  },
)
TableBody.displayName = 'TableBody'

const tableFooterVariants = cva('', {
  variants: {
    variant: {
      default: 'bg-neutral-100 border-t font-medium [&>tr]:last:border-b-0',
      light:
        'border-t font-medium [&>tr]:last:border-b-0 [&>tr]:last:hover:bg-transparent [&>tr]:last:!bg-transparent',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
})

type TableFooterProps = React.HTMLAttributes<HTMLTableSectionElement> &
  VariantProps<typeof tableFooterVariants>

const TableFooter = React.forwardRef<HTMLTableSectionElement, TableFooterProps>(
  (props, ref) => {
    const { className, variant, ...rest } = props
    return (
      <tfoot
        ref={ref}
        className={cx(tableFooterVariants({ variant }), className)}
        {...rest}
      />
    )
  },
)
TableFooter.displayName = 'TableFooter'

const tableRowVariants = cva('', {
  variants: {
    variant: {
      default:
        'hover:bg-neutral-100 data-[state=selected]:bg-neutral-100 transition-colors',
      light: 'hover:bg-neutral-50 transition-colors',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
})

type TableRowProps = React.HTMLAttributes<HTMLTableRowElement> &
  VariantProps<typeof tableRowVariants>

const TableRow = React.forwardRef<HTMLTableRowElement, TableRowProps>(
  (props, ref) => {
    const { className, variant, ...rest } = props
    return (
      <tr
        ref={ref}
        className={cx(tableRowVariants({ variant }), className)}
        {...rest}
      />
    )
  },
)
TableRow.displayName = 'TableRow'

const tableHeadVariants = cva('', {
  variants: {
    variant: {
      default:
        'text-slate-500 h-10 px-2 text-left align-middle font-medium [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]',
      light:
        'h-10 px-4 py-3 text-left text-xs font-light text-slate-100 uppercase tracking-wider',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
})

type TableHeadProps = React.ThHTMLAttributes<HTMLTableCellElement> &
  VariantProps<typeof tableHeadVariants>

const TableHead = React.forwardRef<HTMLTableCellElement, TableHeadProps>(
  (props, ref) => {
    const { className, variant, ...rest } = props
    return (
      <th
        ref={ref}
        className={cx(tableHeadVariants({ variant }), className)}
        {...rest}
      />
    )
  },
)
TableHead.displayName = 'TableHead'

const tableCellVariants = cva('', {
  variants: {
    variant: {
      default:
        'px-2 py-3 align-middle [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]',
      light: 'px-4 py-4 text-gray-900 whitespace-nowrap',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
})

type TableCellProps = React.TdHTMLAttributes<HTMLTableCellElement> &
  VariantProps<typeof tableCellVariants>

const TableCell = React.forwardRef<HTMLTableCellElement, TableCellProps>(
  (props, ref) => {
    const { className, variant, ...rest } = props
    return (
      <td
        ref={ref}
        className={cx(tableCellVariants({ variant }), className)}
        {...rest}
      />
    )
  },
)
TableCell.displayName = 'TableCell'

type TableCaptionProps = React.HTMLAttributes<HTMLTableCaptionElement> & {
  className?: string
}

const TableCaption = React.forwardRef<
  HTMLTableCaptionElement,
  TableCaptionProps
>((props, ref) => {
  const { className, ...rest } = props
  return (
    <caption
      ref={ref}
      className={cx('text-slate-300 mt-4 text-sm', className)}
      {...rest}
    />
  )
})
TableCaption.displayName = 'TableCaption'

export type Column = {
  getFilterValue: () => Value | DateRangeFilterValue | undefined
  setFilterValue: (value?: Value | DateRangeFilterValue) => void
  getIsSorted: () => 'asc' | 'desc' | undefined
  toggleSorting: (ascending: boolean) => void
}

type TableHeaderFilterProps = {
  column: Column
  label: string
  options: LabelType[]
  className?: string
  children?: React.ReactNode
}

const TableHeaderFilter = React.forwardRef<
  HTMLButtonElement,
  TableHeaderFilterProps
>((props: TableHeaderFilterProps, ref) => {
  const { column, label, options, className, children } = props
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild ref={ref}>
        <Button
          variant='ghost'
          className='w-full justify-between rounded-none pl-0 focus-visible:ring-transparent'
        >
          {children || label}
          {column.getFilterValue() === undefined ? (
            <FunnelIcon className='size-4 text-slate-300' />
          ) : (
            <FunnelSimpleIcon className='size-4 text-slate-300' />
          )}
        </Button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content align='end' className={cx('bg-white', className)}>
        <DropdownMenu.CheckboxItem
          onClick={() => {
            column.setFilterValue(undefined)
          }}
          checked={column.getFilterValue() === undefined}
        >
          Tous
        </DropdownMenu.CheckboxItem>
        {options.map((item) => (
          <DropdownMenu.CheckboxItem
            key={item.value}
            className='capitalize'
            onClick={() => {
              column.setFilterValue(item.value)
            }}
            checked={column.getFilterValue() === item.value}
          >
            {item.label}
          </DropdownMenu.CheckboxItem>
        ))}
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  )
})
TableHeaderFilter.displayName = 'TableHeaderFilter'

type SortIconProps = {
  direction: 'asc' | 'desc' | undefined
}

const SortIcon = (props: SortIconProps) => {
  const { direction } = props
  if (direction === 'asc') {
    return <SortDescendingIcon className='ml-2 size-4 text-slate-300' />
  }
  if (direction === 'desc') {
    return <SortAscendingIcon className='ml-2 size-4 text-slate-300' />
  }
  return <ListIcon className='ml-2 size-4 text-slate-300' />
}

type TableHeaderSortProps = {
  column: Column
  before?: boolean
  className?: string
  children?: React.ReactNode
}

const TableHeaderSort = (props: TableHeaderSortProps) => {
  const { column, before, className, children } = props
  return (
    <Button
      variant='ghost'
      className={cx('flex w-full justify-between pl-0', className)}
      onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
    >
      {before && <SortIcon direction={column.getIsSorted()} />}
      {children}
      {!before && <SortIcon direction={column.getIsSorted()} />}
    </Button>
  )
}

export function dateRangeFilterFn(
  row: any,
  columnId: string,
  filterValue: any,
): boolean {
  if (!filterValue) return true

  const cellValue = row.getValue(columnId)
  if (!cellValue) return false

  // different date formats
  const dateFormats = [
    'yyyy-MM-dd',
    'dd/MM/yyyy HH:mm',
    'dd/MM/yyyy',
    'MM/dd/yyyy',
    'yyyy-MM-dd HH:mm:ss',
    'yyyy-MM-dd HH:mm',
    'dd/MM/yyyy à HH:mm:ss',
    'dd/MM/yyyy à HH:mm',
    "dd/MM/yyyy à HH'h'mm",
    "dd/MM/yyyy à HH'h'mmss",
  ]

  let rowDate: Date | null = null

  // handle Date instance, timestamp, or ISO strings directly
  if (cellValue instanceof Date && !Number.isNaN(cellValue.getTime())) {
    rowDate = cellValue
  } else if (typeof cellValue === 'number') {
    const asDate = new Date(cellValue)
    if (!Number.isNaN(asDate.getTime())) rowDate = asDate
  } else if (typeof cellValue === 'string') {
    // Try ISO parsing first
    try {
      const isoParsed = parseISO(cellValue)
      if (isoParsed && !Number.isNaN(isoParsed.getTime())) {
        rowDate = isoParsed
      }
    } catch {}
  }

  // If not parsed yet, try known display formats
  if (!rowDate) {
    for (const format of dateFormats) {
      try {
        const parsed = parse(cellValue as string, format, new Date())
        if (
          parsed &&
          !Number.isNaN(parsed.getTime()) &&
          parsed.getFullYear() > 1900
        ) {
          rowDate = parsed
          break
        }
      } catch {
        continue
      }
    }
  }

  if (!rowDate) return false

  rowDate.setHours(0, 0, 0, 0)

  if (filterValue.mode === 'after') {
    if (!filterValue.from) return true
    const from = new Date(filterValue.from)
    from.setHours(0, 0, 0, 0)
    return rowDate >= from
  }

  if (filterValue.mode === 'before') {
    if (!filterValue.to) return true
    const to = new Date(filterValue.to)
    to.setHours(23, 59, 59, 999)
    return rowDate <= to
  }

  if (!filterValue.from) return true

  const from = new Date(filterValue.from)
  const to = filterValue.to ? new Date(filterValue.to) : from

  from.setHours(0, 0, 0, 0)
  to.setHours(23, 59, 59, 999)

  return rowDate >= from && rowDate <= to
}

type TableDateFilterProps = {
  column: Column
  label: string
  dateFormat?: (date: Date) => string
  className?: string
  children?: React.ReactNode
  isPeriodicCalendar?: boolean
  isNormalCalendar?: boolean
  isDisabled?: boolean
  periodCalendarClassName?: string
  normalCalendarClassName?: string
  language?: SupportedLanguages
  reactSelectTheme?: typeof reactSelectTheme
  reactSelectStyles?: DateRangePickerReactSelectStyles
  allowOpenBounds?: boolean
}

const TableDateFilter = (props: TableDateFilterProps) => {
  const { column, reactSelectTheme, reactSelectStyles, ...rest } = props
  const filterValue = column.getFilterValue() as
    | DateRangeFilterValue
    | undefined

  const handleChange = (range: DateRangeFilterValue | undefined) => {
    column.setFilterValue(range)
  }

  return (
    <DateRangePicker
      value={filterValue}
      onChange={handleChange}
      reactSelectTheme={reactSelectTheme}
      reactSelectStyles={reactSelectStyles}
      buttonClassName='w-full flex justify-between'
      {...rest}
    />
  )
}
TableDateFilter.displayName = 'TableDateFilter'

type Option = {
  label: string
  onClick: () => void
  disabled?: boolean
  icon?: string | React.ReactNode
  className?: string
}

type TableRowActionsProps = {
  options: Option[]
  className?: string
  iconClassName?: string
}

const TableRowActions = (props: TableRowActionsProps) => {
  const { options, className, iconClassName } = props
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <div className={(cx('flex justify-end'), className)}>
          <Button variant='ghost' className='size-6 p-0'>
            <span className='sr-only'>Ouvrir le menu</span>
            <span className={iconClassName}>
              <DotsThreeVerticalIcon className='size-6 text-slate-300' />
            </span>
          </Button>
        </div>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content align='end' className='bg-white'>
        {options.map((option) => (
          <DropdownMenu.Item
            key={option.label}
            onClick={option.onClick}
            disabled={option.disabled}
            className={option.className}
          >
            {option.icon}
            {option.label}
          </DropdownMenu.Item>
        ))}
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  )
}

type TableActionsProps = {
  options: Option[]
  className?: string
  iconsClassName?: string
}

const TableActions = (props: TableActionsProps) => {
  const { options, className, iconsClassName } = props
  return (
    <div
      className={cx('w-full flex items-center justify-end gap-3', className)}
    >
      {options.map((option) => {
        const icon = option.icon ?? option.label

        if (typeof icon === 'string') {
          return (
            <DynamicIcon
              key={option.label}
              name={icon}
              onClick={option.onClick}
              className={iconsClassName}
            />
          )
        }

        return (
          <button
            key={option.label}
            type='button'
            onClick={option.onClick}
            disabled={option.disabled}
            className={cx(
              'inline-flex items-center border-0 bg-transparent p-0 disabled:cursor-not-allowed',
              iconsClassName,
            )}
          >
            {icon}
          </button>
        )
      })}
    </div>
  )
}

const tableFilterFn = (
  row: Row<any>,
  columnId: string,
  filterValue: string,
) => {
  return row.original[columnId] === filterValue
}

export const Table: {
  Root: typeof TableRoot
  Header: typeof TableHeader
  Body: typeof TableBody
  Footer: typeof TableFooter
  Head: typeof TableHead
  Row: typeof TableRow
  Cell: typeof TableCell
  Caption: typeof TableCaption
  HeaderFilter: typeof TableHeaderFilter
  HeaderSort: typeof TableHeaderSort
  RowActions: typeof TableRowActions
  Actions: typeof TableActions
  DateFilter: typeof TableDateFilter
  dateRangeFilterFn: typeof dateRangeFilterFn
  filterFn: typeof tableFilterFn
} = {
  Root: TableRoot,
  Header: TableHeader,
  Body: TableBody,
  Footer: TableFooter,
  Head: TableHead,
  Row: TableRow,
  Cell: TableCell,
  Caption: TableCaption,
  HeaderFilter: TableHeaderFilter,
  HeaderSort: TableHeaderSort,
  RowActions: TableRowActions,
  Actions: TableActions,
  DateFilter: TableDateFilter,
  dateRangeFilterFn: dateRangeFilterFn,
  filterFn: tableFilterFn,
}
